import { ReactNode } from 'react';
import { cn } from '@/utils/cn';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export interface KpiItem {
  title: string;
  value: string | number;
  suffix?: string;
  icon: any; // Lucide icon
  color: string;
  bg: string;
  trend: 'up' | 'down' | 'neutral';
  trendValue: string;
  chartColor: string;
  chartData: number[]; // e.g. [40, 30, 50, ...]
}

interface KpiGridProps {
  kpis: KpiItem[];
  isLoading?: boolean;
}

export function KpiGrid({ kpis, isLoading }: KpiGridProps) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
      {kpis.map((item, idx) => (
        <div 
          key={idx} 
          className="bg-white rounded-2xl border border-slate-100 p-4 shadow-[0_2px_10px_-3px_rgba(6,81,237,0.05)] hover:shadow-[0_8px_20px_-6px_rgba(6,81,237,0.1)] transition-all flex flex-col justify-between group"
        >
          <div className="flex justify-between items-start mb-6">
            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0", item.bg, item.color)}>
              <item.icon size={20} strokeWidth={2} />
            </div>
            
            {/* Trend Pill */}
            <div className={cn(
              "flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold tracking-wide",
              item.trend === 'up' ? 'bg-emerald-50 text-emerald-600' : 
              item.trend === 'down' ? 'bg-emerald-50 text-emerald-600' : // Custom rule context
              'bg-slate-100 text-slate-600'
            )}>
              {item.trend === 'up' && <TrendingUp className="w-3 h-3" />}
              {item.trend === 'down' && <TrendingDown className="w-3 h-3" />}
              {item.trend === 'neutral' && <Minus className="w-3 h-3" />}
              {item.trendValue}
            </div>
          </div>
          
          <div>
            <h3 className="text-[11px] font-medium text-slate-500 uppercase tracking-wider mb-1 line-clamp-1">
              {item.title}
            </h3>
            <div className="flex items-end justify-between gap-2">
              <div className="flex items-baseline gap-1 min-w-0">
                {isLoading ? (
                  <div className="h-6 w-16 bg-slate-200 animate-pulse rounded" />
                ) : (
                  <>
                    <span className="text-lg font-bold text-slate-800 tracking-tight truncate">{item.value}</span>
                    {item.suffix && <span className="text-[11px] font-medium text-slate-400 whitespace-nowrap">{item.suffix}</span>}
                  </>
                )}
              </div>
              
              {/* Mini Chart (CSS Bars) */}
              <div className="flex items-end gap-0.5 h-6 opacity-60 group-hover:opacity-100 transition-opacity shrink-0">
                {(() => {
                  const maxH = Math.max(100, ...item.chartData);
                  return item.chartData.map((h, i) => (
                    <div 
                      key={i} 
                      className={cn("w-1 rounded-full transition-all duration-500", item.chartColor)} 
                      style={{ height: `${(h / maxH) * 100}%` }}
                    />
                  ));
                })()}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
